import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LocalText from '../common/LocalText';
import { validateWireDocumentFile, ACCEPTED_WIRE_DOCUMENT_ACCEPT } from '../../utils/wireDocumentValidation';

// Finance-only dialog used by DepositDetail's "Replace" action (document already on file) and
// its "Upload" fallback action (legacy deposit missing a document) — same dialog, different copy.
const WireDocumentUploadDialog = ({ open, mode = 'upload', uploading = false, onClose, onConfirm }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  // Reset picked-file state each time the dialog is (re)opened.
  useEffect(() => {
    if (open) { setFile(null); setError(''); }
  }, [open]);

  const handlePick = () => inputRef.current?.click();

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = ''; // allow re-picking the same file name after an error
    if (!picked) return;
    const validationError = validateWireDocumentFile(picked);
    if (validationError) { setError(validationError); setFile(null); return; }
    setError('');
    setFile(picked);
  };

  const handleClose = () => { if (!uploading) onClose(); };
  const handleConfirm = () => { if (file && !uploading) onConfirm(file); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
        <LocalText>{mode === 'replace' ? 'Replace Transfer Document' : 'Upload Transfer Document'}</LocalText>
      </DialogTitle>
      <DialogContent dividers>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_WIRE_DOCUMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Box
          component="button"
          type="button"
          onClick={handlePick}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, width: '100%',
            border: `1px dashed ${file ? '#86efac' : '#94a3b8'}`, borderRadius: '8px',
            bgcolor: file ? '#f0fdf4' : '#f8fafc', color: file ? '#15803d' : '#0a326d',
            fontWeight: 600, fontSize: 12.5, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <UploadFileIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {file ? file.name : <LocalText>Choose PDF, PNG, or JPEG (max 5 MB)</LocalText>}
          </Box>
        </Box>
        {error && (
          <Typography fontSize={12} sx={{ color: '#dc2626', mt: 1 }}>{error}</Typography>
        )}
        {mode === 'replace' && (
          <Typography fontSize={11} sx={{ color: '#64748b', mt: 1.5, lineHeight: 1.6 }}>
            <LocalText>Uploading a new file replaces the current document for this deposit — there is no version history.</LocalText>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box component="button" onClick={handleClose} disabled={uploading}
          sx={{ px: 2, height: 36, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b' }}>
          <LocalText>Cancel</LocalText>
        </Box>
        <Box component="button" onClick={handleConfirm} disabled={!file || uploading}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2.25, height: 36, border: 'none', borderRadius: '8px', bgcolor: '#0a326d', cursor: (!file || uploading) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, color: 'white', opacity: (!file || uploading) ? 0.6 : 1 }}>
          {uploading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <LocalText>{mode === 'replace' ? 'Replace' : 'Upload'}</LocalText>}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WireDocumentUploadDialog;
